let currentAudio = null;
let stopFn = null;

// Inward trim applied to the end of a word clip (seconds).
export const WORD_TRIM = 0.04;

// Lead-in / tail-out so a segment/sentence/paragraph doesn't start or end abruptly.
export const PRE_ROLL = 0.15, POST_ROLL = 0.15;

export function stopAll() {
  if (stopFn) { stopFn(); stopFn = null; }
  if (currentAudio) { currentAudio.pause(); currentAudio = null; }
  stopWordSrc();
}

// --- Word playback: Web Audio, sample-accurate ---------------------------
// HTMLAudioElement seeks land on AAC packet boundaries (±46ms at 22.05kHz)
// and pause() adds 20-80ms — through that player a word-tiled span like
// ka 3.180-3.351 plays ≈[3.13..3.40] = "skanii". Word taps therefore bypass
// seeking entirely: decode the clip once, play the exact sample range on a
// BufferSource with a scheduled stop. (Historic hand-dragged borders were
// ear-tuned THROUGH the quantizing player; acoustically-true data needs a
// sample-accurate one.)
let actx = null, wordSrc = null, wordToken = null;
const wordBufs = new Map();            // url -> Promise<AudioBuffer>
function wordBuffer(url) {
  if (!wordBufs.has(url)) {
    if (wordBufs.size > 24) wordBufs.clear();   // clips are small; tiny cache
    wordBufs.set(url, fetch(url).then(r => r.arrayBuffer()).then(b => actx.decodeAudioData(b)));
  }
  return wordBufs.get(url);
}
function stopWordSrc() {
  wordToken = null;
  if (wordSrc) { try { wordSrc.stop(); } catch (_) {} wordSrc = null; }
  if (wordEl) { wordEl.pause(); if (wordElDetach) { wordElDetach(); wordElDetach = null; } }
}

// Last word-playback failure, for diagnosis from the console: the Web Audio path
// is the only one in this file that needs CORS and a working decoder, so it is
// the only one that can fail on a device where every other clip plays. It used
// to fail into `.catch(() => {})` — no sound, no error, nothing to go on.
export let lastWordError = null;
export function wordAudioDiagnostics() {
  return {
    lastWordError: lastWordError && String(lastWordError),
    ctxState: actx ? actx.state : 'not created',
    cachedClips: wordBufs.size,
    usingFallback: fallbackActive,
    engine: wordEngine(),
  };
}
if (typeof window !== 'undefined') window.__wordAudio = wordAudioDiagnostics;

// Run `begin` once the element knows its duration, so we can clamp the seek.
function whenReady(audio, begin) {
  if (audio.readyState >= 1 && audio.duration) begin();
  else audio.addEventListener('loadedmetadata', begin, { once: true });
}

// Double-play rule (user spec): a loading clip must not start while ANOTHER
// clip is live — but an empty slot means nothing is playing, so re-register
// and start (a bare stopAll between request and metadata must not kill the
// play: that killed replay and left views stuck "playing"). Returns false
// when the caller must yield; yielding callers fire their end-callback so
// no UI state ever sticks.
function claimSlot(audio, restoreStopFn) {
  if (currentAudio === audio) return true;
  if (currentAudio) return false;          // a newer clip is live — yield
  currentAudio = audio;                    // slot empty — reclaim and play
  if (restoreStopFn) stopFn = restoreStopFn;
  return true;
}

// Poll currentTime every animation frame (timeupdate alone is ~4 Hz).
function rafTicker(tick) {
  let id;
  const loop = () => { tick(); id = requestAnimationFrame(loop); };
  id = requestAnimationFrame(loop);
  return () => cancelAnimationFrame(id);
}

// Which engine plays a word tap. The Web Audio path is sample-accurate and is
// what the timings were tuned against, so it stays the default on desktop.
// On touch devices it is switched off by default, and NOT because of a bug we
// found in it: on the Android phone this app is actually read on, a word tap
// fetches 200, decodes, and starts on a running context — every observable
// step succeeds — and no sound comes out. Web Audio is the ONLY engine word
// taps use; sentences, segments and paragraphs all go through <audio> and are
// audible on that same device. So the failure is below the API surface, where
// no amount of error handling reaches it, and the fix is to stop using the
// engine that goes silent rather than to keep instrumenting it. The <audio>
// path lands seeks on AAC packet boundaries (±46ms) — audibly looser at word
// edges, and the reason it is not used everywhere — but it makes a sound.
// '' = auto, 'buffer' = force Web Audio, 'element' = force <audio>.
const WORD_ENGINE_KEY = 'wordEngine';
export function wordEngine() {
  let pref = '';
  try { pref = window.localStorage.getItem(WORD_ENGINE_KEY) || ''; } catch (_) {}
  if (pref === 'buffer' || pref === 'element') return pref;
  const coarse = typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;
  return coarse ? 'element' : 'buffer';
}
export function setWordEngine(pref) {
  try {
    if (pref) window.localStorage.setItem(WORD_ENGINE_KEY, pref);
    else window.localStorage.removeItem(WORD_ENGINE_KEY);
  } catch (_) {}
}

export function playWord(url, wordStart, wordEnd) {
  stopAll();
  const me = {};
  wordToken = me;
  if (wordEngine() === 'element') { playWordViaElement(url, wordStart, wordEnd, me); return; }
  if (!actx) actx = new (window.AudioContext || window.webkitAudioContext)();
  if (actx.state === 'suspended') actx.resume();
  wordBuffer(url).then((buf) => {
    if (wordToken !== me) return;                       // superseded while decoding
    if (currentAudio && !currentAudio.paused) return;   // a range is live — yield
    // Fixed 0.04 chops a third off a 0.12s word ("ka") — trim proportionally.
    const trim = Math.min(WORD_TRIM, 0.15 * Math.max(0, wordEnd - wordStart));
    const start = Math.max(0, Math.min(wordStart, buf.duration));
    const dur = Math.max(0.02, Math.min(wordEnd - trim, buf.duration) - start);
    const s = actx.createBufferSource();
    s.buffer = buf;
    s.connect(actx.destination);
    s.onended = () => { if (wordSrc === s) wordSrc = null; };
    wordSrc = s;
    s.start(0, start, dur);                             // exact samples, scheduled stop
  }).catch((err) => {
    // Do NOT swallow. wordBufs caches the PROMISE, so a cached rejection would
    // also make this clip fail forever — drop it, then play the span through an
    // <audio> element, which needs neither CORS nor decodeAudioData. Less
    // precise at the edges than the buffer path (that is why it is not the
    // default), but audible beats silent.
    wordBufs.delete(url);
    lastWordError = err;
    if (wordToken !== me) return;
    playWordViaElement(url, wordStart, wordEnd, me);
  });
}

// --- Word playback fallback: plain <audio> ---------------------------------
// One reused, gesture-activated element (same reason playRange/playChain reuse
// theirs: a fresh Audio() per play needs its own gesture in autoplay-strict
// states, and the rejection is swallowed).
let wordEl = null, wordElUrl = null, wordElDetach = null, fallbackActive = false;
function playWordViaElement(url, wordStart, wordEnd, me) {
  fallbackActive = true;
  if (!wordEl) { wordEl = new Audio(url); wordElUrl = url; }
  else if (wordElUrl !== url) { wordEl.pause(); wordEl.src = url; wordElUrl = url; wordEl.load(); }
  whenReady(wordEl, () => {
    if (wordToken !== me) return;                       // superseded while loading
    if (currentAudio && !currentAudio.paused) return;   // a range is live — yield
    const dur = wordEl.duration || 0;
    const start = Math.max(0, Math.min(wordStart, Math.max(0, dur - 0.02)));
    // Same proportional inward trim the buffer path applies, so a word doesn't
    // play longer here than it does on desktop. Words are tiled end-to-start,
    // so anything past `end` is already the next word.
    const trim = Math.min(WORD_TRIM, 0.15 * Math.max(0, wordEnd - wordStart));
    const end = Math.min(wordEnd - trim, dur || wordEnd);
    // timeupdate is ~4 Hz: a 250ms granularity overruns a 0.3s word into the
    // next one, which is most of them. Poll per frame instead — the same
    // ticker the range players use for highlighting.
    let stopTick = () => {};
    const detach = () => {
      stopTick();
      if (wordElDetach === detach) wordElDetach = null;
    };
    if (wordElDetach) wordElDetach();
    wordEl.currentTime = start;
    stopTick = rafTicker(() => {
      if (wordToken !== me) { detach(); return; }        // superseded — let the new tap own it
      if (wordEl.currentTime >= end) { detach(); wordEl.pause(); }
    });
    wordElDetach = detach;
    wordEl.play().catch((e) => { lastWordError = e; });
  });
}

export function playSentence(url, onTimeUpdate, onEnded) {
  stopAll();
  const audio = new Audio(url);
  currentAudio = audio;
  const handleTime = () => onTimeUpdate && onTimeUpdate(audio.currentTime);
  const handleEnd  = () => { onEnded && onEnded(); stopFn = null; currentAudio = null; };
  audio.addEventListener('timeupdate', handleTime);
  audio.addEventListener('ended', handleEnd);
  audio.play().catch(() => {});
  stopFn = () => {
    audio.pause();
    audio.removeEventListener('timeupdate', handleTime);
    audio.removeEventListener('ended', handleEnd);
    currentAudio = null;
  };
  return stopFn;
}

// Skip windows for long inter-word silences (picture-book pauses inside an
// unpunctuated sentence, e.g. Paiwan's 32s musical interlude): keep a natural
// 0.5s trail-off and 0.2s lead-in, jump the dead air between. Words must be in
// the same time coordinates the playback uses (clip seconds).
export function gapSkips(words, minGap = 2.0, keepHead = 0.5, keepTail = 0.2) {
  const out = [];
  for (let i = 0; i + 1 < (words || []).length; i++) {
    const gap = words[i + 1].start - words[i].end;
    if (gap > minGap) out.push([words[i].end + keepHead, words[i + 1].start - keepTail]);
  }
  return out;
}

// --- Range playback: ONE reused <audio> element ---------------------------
// A fresh Audio() per range needs a user gesture for each .play() on iOS (and
// in Chrome's autoplay-strict states): play() fires after loadedmetadata,
// outside the tap's activation window, and the rejection is swallowed — so
// autoplay-on-advance worked only when the clip came from cache. Same fix
// playChain already carries: reuse one gesture-activated element, swapping src
// only when the clip changes. rangeToken is the per-call claim on the shared
// element (the double-play rule between two playRange calls); rangeDetach
// strips the previous call's listeners so they can't fire on the next range.
let rangeEl = null, rangeElUrl = null, rangeToken = null, rangeDetach = null;
function rangeElement(url) {
  if (rangeDetach) rangeDetach();
  if (!rangeEl) { rangeEl = new Audio(url); rangeElUrl = url; return rangeEl; }
  if (rangeElUrl !== url) {
    rangeEl.pause();
    rangeEl.src = url; rangeElUrl = url;               // same element: no new gesture needed
    rangeEl.load();
  }
  return rangeEl;
}

// Play [startTime, endTime] of a file. onTimeUpdate gets time relative to the
// (clamped) start. The seek is clamped to the clip so an out-of-range start
// never silently restarts from 0. `skips` = [[a,b], …] silent spans to jump.
export function playRange(url, startTime, endTime, onTimeUpdate, onEnded, preRoll = PRE_ROLL, postRoll = POST_ROLL, skips = []) {
  stopAll();
  const audio = rangeElement(url);
  const me = {};
  rangeToken = me;
  currentAudio = audio;
  const loadStop = () => { audio.pause(); currentAudio = null; };
  stopFn = loadStop;   // covers the metadata-loading window
  whenReady(audio, () => {
    // Superseded while loading (a newer playRange took the shared element), or
    // another clip is live — yield, but ALWAYS fire onEnded so the caller's
    // playing-state resets (a silent abort left ▶ buttons stuck "playing").
    if (rangeToken !== me) { onEnded && onEnded(); return; }
    if (!claimSlot(audio, loadStop)) { onEnded && onEnded(); return; }
    const dur = audio.duration || 0;
    const want = startTime ?? 0;
    const start = Math.max(0, Math.min(want - preRoll, Math.max(0, dur - 0.02)));   // lead-in
    const end = endTime != null ? Math.min(endTime + postRoll, dur) : null;          // tail-out
    audio.currentTime = start;
    const detach = () => {
      audio.removeEventListener('timeupdate', checkEnd);
      audio.removeEventListener('ended', handleEnd);
      if (rangeDetach === detach) rangeDetach = null;
    };
    const checkEnd = () => {
      for (const [a, b] of skips) {
        if (audio.currentTime >= a && audio.currentTime < b) { audio.currentTime = b; return; }
      }
      if (end != null && audio.currentTime >= end) {
        detach();
        audio.pause();
        // Clear registration BEFORE onEnded: chained callers start the next
        // clip inside onEnded, and clearing after would clobber its stopFn.
        stopFn = null; currentAudio = null;
        onEnded && onEnded();
      } else {
        onTimeUpdate && onTimeUpdate(audio.currentTime - want);   // time relative to the ORIGINAL start
      }
    };
    // 'ended' always attached: a clip shorter than `end` must still clean the
    // shared element up (a stale listener here would fire into the NEXT range).
    const handleEnd = () => { detach(); stopFn = null; currentAudio = null; onEnded && onEnded(); };
    audio.addEventListener('timeupdate', checkEnd);
    audio.addEventListener('ended', handleEnd);
    rangeDetach = detach;
    audio.play().catch(() => {});
    stopFn = () => {
      detach();
      audio.pause();
      currentAudio = null;
    };
  });
  return () => stopAll();
}

export function playSegment(url, startTime, endTime, onEnded) {
  return playRange(url, startTime, endTime, null, onEnded);
}

// Chained playback of [{url, start, end, preRoll, postRoll, skips}] on ONE
// <audio> element. A fresh Audio() per part needs a user gesture for each
// .play() on iOS (and in some Chrome autoplay states) — the rejection was
// silently swallowed and 段 died after its first sentence. Reusing the
// gesture-activated element (swapping src only when the clip changes) is the
// standard workaround, and gives seamless part joins within one clip for free.
export function playChain(items, onTimeUpdate, onItem, onDone) {
  stopAll();
  if (!items || !items.length) { onDone && onDone(); return () => {}; }
  const audio = new Audio(items[0].url);
  let idx = -1, it = null, curUrl = items[0].url;
  currentAudio = audio;
  const detach = () => {
    audio.removeEventListener('timeupdate', checkEnd);
    audio.removeEventListener('ended', handleEnd);
  };
  const chainStop = () => { detach(); audio.pause(); currentAudio = null; };
  stopFn = chainStop;
  const finish = () => { detach(); audio.pause(); stopFn = null; currentAudio = null; onDone && onDone(); };
  const begin = () => {
    if (!claimSlot(audio, chainStop)) { detach(); onDone && onDone(); return; }   // another clip is live — yield
    const dur = audio.duration || 0;
    const start = Math.max(0, Math.min((it.start ?? 0) - (it.preRoll ?? PRE_ROLL), Math.max(0, dur - 0.02)));
    audio.currentTime = start;
    onItem && onItem(idx);
    audio.play().catch(() => {});
  };
  const next = () => {
    idx += 1;
    if (idx >= items.length) { finish(); return; }
    it = items[idx];
    if (it.url !== curUrl) {
      curUrl = it.url;
      audio.pause();
      audio.src = it.url;                                  // same element: no new gesture needed
      audio.load();
      whenReady(audio, begin);
    } else {
      begin();                                             // same clip: just seek — seamless
    }
  };
  const endAt = () => {
    const dur = audio.duration || 0;
    return it.end != null ? Math.min(it.end + (it.postRoll ?? POST_ROLL), dur) : dur;
  };
  const checkEnd = () => {
    if (!it) return;
    for (const [a, b] of (it.skips || [])) {
      if (audio.currentTime >= a && audio.currentTime < b) { audio.currentTime = b; return; }
    }
    if (audio.currentTime >= endAt()) next();
    else onTimeUpdate && onTimeUpdate(idx, audio.currentTime);
  };
  // Clip ran out before it.end (short file). Guard staleness: after checkEnd
  // already advanced, a queued 'ended' from the old position must not advance
  // again (src swap → readyState 0; same-clip seek → currentTime back below end).
  const handleEnd = () => {
    if (audio.readyState === 0) return;
    if (it && audio.currentTime < endAt() - 0.05) return;
    next();
  };
  audio.addEventListener('timeupdate', checkEnd);
  audio.addEventListener('ended', handleEnd);
  whenReady(audio, () => next());
  return () => stopAll();
}

export function isPlaying() {
  return currentAudio != null && !currentAudio.paused;
}
